import * as THREE from 'three';
import { clamp, damp } from '../utils/math';

const MIN_DISTANCE = 9;
const MAX_DISTANCE = 40;
const MIN_POLAR = 0.55;
const MAX_POLAR = 1.32;

/**
 * Third-person orbit camera that follows a focus point. Looks slightly above
 * the player so the mountains and sky stay in frame.
 */
export class CameraRig {
  azimuth = 0;
  polar = 1.16;
  distance = 21;
  /** When true the camera slowly circles the farm (title screen). */
  attract = false;

  private readonly focus = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private readonly offset = new THREE.Vector3();
  private targetAzimuth = this.azimuth;
  private targetPolar = this.polar;
  private targetDistance = this.distance;
  private initialized = false;

  constructor(readonly camera: THREE.PerspectiveCamera) {}

  rotate(deltaAzimuth: number, deltaPolar: number): void {
    this.targetAzimuth += deltaAzimuth;
    this.targetPolar = clamp(this.targetPolar + deltaPolar, MIN_POLAR, MAX_POLAR);
  }

  zoom(factor: number): void {
    this.targetDistance = clamp(this.targetDistance * factor, MIN_DISTANCE, MAX_DISTANCE);
  }

  /** Current focus point (smoothed). */
  get target(): THREE.Vector3 {
    return this.focus;
  }

  update(dt: number, focusPoint: THREE.Vector3): void {
    if (this.attract) this.targetAzimuth += dt * 0.05;
    if (!this.initialized) {
      this.focus.copy(focusPoint);
      this.initialized = true;
    }
    this.focus.lerp(focusPoint, damp(6, dt));
    this.azimuth += (this.targetAzimuth - this.azimuth) * damp(8, dt);
    this.polar += (this.targetPolar - this.polar) * damp(8, dt);
    this.distance += (this.targetDistance - this.distance) * damp(8, dt);

    // Closer zoom levels tilt the camera towards the horizon for a cosier view.
    const zoomT = (this.distance - MIN_DISTANCE) / (MAX_DISTANCE - MIN_DISTANCE);
    const polar = clamp(this.polar + (1 - zoomT) * 0.12, MIN_POLAR, MAX_POLAR);

    this.offset.setFromSphericalCoords(this.distance, polar, this.azimuth);
    this.camera.position.copy(this.focus).add(this.offset);
    this.look.copy(this.focus);
    this.look.y += 3.2;
    this.camera.lookAt(this.look);
  }

  /** Converts a 2D input direction (x = right, y = forward) into a world XZ vector. */
  screenToWorld(x: number, y: number, out: THREE.Vector3): THREE.Vector3 {
    const sin = Math.sin(this.azimuth);
    const cos = Math.cos(this.azimuth);
    // Forward is from the camera towards the focus (−offset direction).
    return out.set(x * cos - y * sin, 0, -x * sin - y * cos);
  }
}
